import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useDayPicker, useNavigation } from "react-day-picker";
import { CALENDAR_MONTH_WRAPPER_DROPDOWN, CALENDAR_YEAR_DROPDOWN, CALENDAR_YEAR_LABEL_EMPTY } from "../../themes/common/classes";
import { IconBack, IconForward, MenuItem, Select } from "../..";
import BaseButton from "../BaseButton/BaseButton";
import { CalendarContext } from "./CalendarContext";
import { ELEVEN, MONTHS, ZERO } from "./constants";
import STRINGS from "./strings";
/**
 * This NavigationBar component is used to render the Navigation Bar in the Calendar component.
 * It contains the Year Dropdown and the Month Dropdown as well as a next month button and
 * a previous month button.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function NavigationBar() {
  const {
    previousMonth,
    nextMonth,
    goToMonth
  } = useNavigation();
  const {
    componentStrings,
    navigationBarStyles
  } = useContext(CalendarContext);
  const {
    fromDate,
    month,
    onNextClick,
    onPrevClick,
    toDate
  } = useDayPicker();
  const currentMonth = month.getMonth();
  const currentYear = month.getFullYear();
  /**
   * @description Produces a string containing the label for the previous month button
   * @returns {string} The updated label of the previous month button
   */

  const getPreviousMonthLabel = () => {
    let previousMonthIndex, year;

    if (currentMonth === ZERO) {
      previousMonthIndex = ELEVEN;
      year = currentYear - 1;
    } else {
      previousMonthIndex = currentMonth - 1;
      year = currentYear;
    }

    const previousMonthName = MONTHS[previousMonthIndex];
    return `Previous month, ${previousMonthName} ${year}`;
  };
  /**
   * @description Produces a string containing the label for the next month button
   * @returns {string} The updated label of the next month button
   */


  const getNextMonthLabel = () => {
    let nextMonthIndex, year;

    if (currentMonth === ELEVEN) {
      nextMonthIndex = 0;
      year = currentYear + 1;
    } else {
      nextMonthIndex = currentMonth + 1;
      year = currentYear;
    }

    const nextMonthName = MONTHS[nextMonthIndex];
    return `Next month, ${nextMonthName} ${year}`;
  };

  const handlePreviousClick = e => {
    goToMonth(previousMonth);
    /**
     * `onPrevClick` is a function that is passed by the user to the
     * `react-day-picker` component via `Calendar`.
     */

    onPrevClick && onPrevClick(e);
  };

  const handleNextClick = e => {
    goToMonth(nextMonth);
    /**
     * `onNextClick` is a function that is passed by the user to the
     * `react- day-picker` component via `Calendar`.
     */

    onNextClick && onNextClick(e);
  };
  /**
   * This function handles the dropdown changes
   * @param {String} yearValue - selected year value.
   */


  const handleYearChange = _ref => {
    let {
      value: yearValue
    } = _ref;
    const navigationDate = new Date(parseInt(yearValue, 10), currentMonth);
    goToMonth(navigationDate);
  };
  /**
   * This function handles the dropdown changes
   * @param {String} monthValue - selected month value.
   */


  const handleMonthChange = _ref2 => {
    let {
      value: monthValue
    } = _ref2;
    const navigationDate = new Date(currentYear, parseInt(monthValue, 10));
    goToMonth(navigationDate);
  };

  const years = [];

  for (let i = fromDate.getFullYear(); i <= toDate.getFullYear(); i += 1) {
    years.push(i);
  } // Get from and to year.


  const fromYear = fromDate.getFullYear();
  const toYear = toDate.getFullYear(); // Get min and max selected month respectively.

  const defaultMinMonth = fromDate.getMonth();
  const defaultMaxMonth = toDate.getMonth();
  /**
   * As per specs, navigation arrow buttons automatically disable
   * once reaching the end of a navigable date range.
   */

  const currentSelectedYear = month.getFullYear();
  const currentSelectedMonth = month.getMonth();
  const isMinMonth = currentSelectedMonth === ZERO || currentSelectedMonth === defaultMinMonth;
  const isMaxMonth = currentSelectedMonth === ELEVEN || currentSelectedMonth === defaultMaxMonth;
  const isPrevClickDisabled = currentSelectedYear === fromYear && isMinMonth;
  const isNextClickDisabled = currentSelectedYear === toYear && isMaxMonth;
  /**
   * If the selected year is not in the set of years, assign a class that will
   * assign a new width to accommodate width of "Select" label.
   */

  const yearSelectClass = clsx({
    [CALENDAR_YEAR_DROPDOWN]: true,
    [CALENDAR_YEAR_LABEL_EMPTY]: years.indexOf(currentSelectedYear) === -1
  });
  const {
    rootSection,
    navButtons: cssNavButtons,
    navBarDropdownsWrapper,
    navBarDropdowns
  } = navigationBarStyles;
  return _jsxs("section", {
    "aria-label": "calendar options",
    css: rootSection,
    children: [_jsx(BaseButton, {
      "aria-label": getPreviousMonthLabel(),
      centerIcon: _jsx(IconBack, {
        size: "small",
        "aria-hidden": "true"
      }),
      kind: "simple",
      onClick: handlePreviousClick,
      disabled: isPrevClickDisabled,
      buttonStyles: cssNavButtons,
      noBox: true
    }), _jsxs("div", {
      css: navBarDropdownsWrapper,
      children: [_jsx(Select, {
        name: "month",
        value: month.getMonth(),
        onValueChange: handleMonthChange,
        fieldClassName: CALENDAR_MONTH_WRAPPER_DROPDOWN,
        labelHidden: true,
        selectMenuWidth: 120,
        noEllipsisForPlaceholder: true,
        label: componentStrings.monthAriaLabel,
        selectTriggerStyles: _objectSpread({}, navBarDropdowns),
        expandIconSize: "xsmall",
        children: MONTHS.map((eachMonth, i) => _jsx(MenuItem, {
          value: i,
          children: eachMonth
        }, eachMonth))
      }), _jsx(Select, {
        name: "year",
        value: month.getFullYear(),
        onValueChange: handleYearChange,
        fieldClassName: yearSelectClass,
        labelHidden: true,
        selectMenuWidth: 80,
        noEllipsisForPlaceholder: true,
        label: componentStrings.yearAriaLabel,
        selectTriggerStyles: _objectSpread({}, navBarDropdowns),
        expandIconSize: "xsmall",
        children: years.map(year => _jsx(MenuItem, {
          value: year,
          children: year
        }, year))
      })]
    }), _jsx(BaseButton, {
      "aria-label": getNextMonthLabel(),
      centerIcon: _jsx(IconForward, {
        size: "small",
        "aria-hidden": "true"
      }),
      kind: "simple",
      onClick: handleNextClick,
      disabled: isNextClickDisabled,
      buttonStyles: cssNavButtons,
      noBox: true
    })]
  });
}

NavigationBar.propTypes = process.env.NODE_ENV !== "production" ? {
  /* Override for strings used within the component */
  customStrings: PropTypes.shape({
    /**
     * Aria label of the `Month` select control.
     */
    monthAriaLabel: PropTypes.string,

    /**
     * Aria label of the `Year` select control.
     */
    yearAriaLabel: PropTypes.string
  })
} : {};
NavigationBar.defaultProps = {
  customStrings: STRINGS
};
export default NavigationBar;